import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { SEO } from "../components/SEO";
import { Reveal, ClipImage, MaskedLines } from "../components/Reveal";
import { SectionHeader } from "../components/SectionHeader";
import { BeforeAfter } from "../components/BeforeAfter";
import { EvidenceMarquee } from "../components/EvidenceMarquee";
import { TestimonialsMarquee } from "../components/TestimonialsMarquee";
// homepage proof scenes reused on the kitchen-exhaust LP2 (paid-ads landing
// page) only — stats strip, process, evidence standard, client trust +
// standards band, and the national coverage map
import { CredibilityBar } from "../scenes/CredibilityBar";
import { WhyDcc } from "../scenes/WhyDcc";
import { EvidenceScene } from "../scenes/EvidenceScene";
import { ClientProof } from "../scenes/ClientProof";
import { LocationsCompact } from "../scenes/LocationsCompact";
import { ConvertScene } from "../scenes/ConvertScene";
import { OrangeCta } from "../components/OrangeCta";
import { SERVICES } from "../data/services";
import { INDUSTRIES, INDUSTRY_NAME_PAGE } from "../data/industries";
import { PROJECTS } from "../data/work";
import { PRIORITY_MARKETS, ALL_MARKERS } from "../data/locations";
import { EVIDENCE_PAIRS, IMG, imgProps } from "../data/assets";
import { SITE } from "../data/site";
import { track } from "../lib/analytics";
import NotFound from "./NotFound";

const ListBlock = ({ title, items, dark, no }) => (
  <div>
    <p className={dark ? "eyebrow-dark" : "eyebrow"}>{no} — {title}</p>
    <ul className="mt-6 space-y-0">
      {items.map((it, i) => (
        <Reveal as="li" key={it} delay={i * 0.04} className={`flex items-start gap-4 border-b py-4 text-sm leading-relaxed ${dark ? "border-zinc-800 text-zinc-300" : "border-zinc-200 text-zinc-700"}`}>
          <span className={`mt-0.5 font-mono text-xs ${dark ? "text-dcc" : "text-dcc-strong"}`}>{String(i + 1).padStart(2, "0")}</span>
          {it}
        </Reveal>
      ))}
    </ul>
  </div>
);

export default function ServiceDetail({ slugOverride, lpVariant }) {
  // slugOverride serves paid-ads landing-page variants (e.g. /services/<slug>/lp2):
  // identical content, canonical + JSON-LD stay on the main service page, noindex.
  const { slug: routeSlug } = useParams();
  const slug = slugOverride || routeSlug;
  const service = SERVICES.find((s) => s.slug === slug);

  useEffect(() => {
    if (service) track("service_page_view", { service: service.slug, ...(lpVariant ? { variant: lpVariant } : {}) });
  }, [service, lpVariant]);

  if (!service) return <NotFound />;

  const pair = EVIDENCE_PAIRS.find((p) => p.id === service.id);
  const related = INDUSTRIES.filter((i) => i.services.includes(service.id)).slice(0, 6);
  const stories = PROJECTS.filter((p) => p.service === service.id);
  const markets = ALL_MARKERS.filter((m) => PRIORITY_MARKETS.includes(m.id));
  const idx = SERVICES.indexOf(service);
  const next = SERVICES[(idx + 1) % SERVICES.length];

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: service.name,
      provider: { "@type": "Organization", name: SITE.name, url: SITE.url },
      areaServed: "IN",
      description: service.tagline,
      url: `${SITE.url}/services/${service.slug}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: service.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE.url },
        { "@type": "ListItem", position: 2, name: "Services", item: `${SITE.url}/services` },
        { "@type": "ListItem", position: 3, name: service.name, item: `${SITE.url}/services/${service.slug}` },
      ],
    },
  ];

  // LP2 (paid-ads) landing pages carry the homepage proof sections and the
  // moving evidence/testimonials galleries; the hero stays per-service, untouched
  const isLp2 = lpVariant === "lp2";

  return (
    <>
      <SEO title={service.seo.title} description={service.seo.description} path={`/services/${service.slug}`} jsonLd={jsonLd} image={IMG[service.image].src} robots={lpVariant ? "noindex, follow" : undefined} />

      {/* HERO */}
      <section className="noise-dark relative overflow-hidden pt-32 text-zinc-100 md:pt-44" aria-labelledby="service-heading">
        <div className="absolute inset-0 grid-lines-dark" aria-hidden="true" />
        <span aria-hidden="true" className="pointer-events-none absolute -right-4 bottom-0 select-none font-display text-[26vw] font-bold leading-none text-zinc-800/60 lg:text-[18vw]">{service.num}</span>
        <div className="container-x relative grid gap-12 pb-16 md:pb-24 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <nav aria-label="Breadcrumb" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">
                <Link to="/services" className="transition-colors hover:text-dcc">Services</Link>
                <span aria-hidden="true">/</span>
                <span className="text-dcc">{service.num}</span>
              </nav>
            </Reveal>
            <MaskedLines
              lines={[service.name.replace("Commercial ", ""), "— done commercially."]}
              delay={0.15}
              className="h-display mt-8 text-4xl text-zinc-50 sm:text-5xl lg:text-6xl"
            />
            <h1 id="service-heading" className="sr-only">{service.name}</h1>
            <Reveal delay={0.4}>
              <p className="mt-8 max-w-xl text-base leading-relaxed text-zinc-400 md:text-lg">{service.tagline}</p>
            </Reveal>
            {/* AEO: direct 40–60-word summary — who we serve, what is cleaned,
                how it is verified, where it is available */}
            <Reveal delay={0.45}>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-zinc-500 md:text-base" data-testid="service-summary">{service.summary}</p>
            </Reveal>
            <Reveal delay={0.5}>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to={`/request-survey?service=${service.slug}&source=service-page`} data-testid="service-survey-cta" className="btn-primary">{SITE.cta.label} <ArrowUpRight size={15} aria-hidden="true" /></Link>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <ClipImage src={IMG[service.image].src} alt={IMG[service.image].alt} ratio="aspect-[4/3]" className="border border-zinc-800" eager />
          </div>
        </div>
      </section>

      {/* PROBLEM + WHY */}
      <section className="bg-paper py-24 md:py-32">
        <div className="container-x">
          <SectionHeader no="A" eyebrow="The system problem" title="What is actually happening inside." />
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-6">
              {service.problem.map((p, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <p className="mb-6 text-base leading-relaxed text-zinc-700 md:text-lg">{p}</p>
                </Reveal>
              ))}
            </div>
            <div className="lg:col-span-6">
              <div className="grid gap-px border border-zinc-200 bg-zinc-200">
                {service.whyMatters.map((w, i) => (
                  <Reveal key={w.h} delay={i * 0.06}>
                    <div className="bg-paper p-7">
                      <h3 className="font-display text-xl font-semibold uppercase tracking-tight text-charcoal"><span className="mr-3 font-mono text-xs text-dcc-strong">{String(i + 1).padStart(2, "0")}</span>{w.h}</h3>
                      <p className="mt-3 text-sm leading-relaxed text-zinc-600">{w.p}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* APPROACH (dark) */}
      <section className="noise-dark relative overflow-hidden py-24 text-zinc-100 md:py-32">
        <div className="absolute inset-0 grid-lines-dark" aria-hidden="true" />
        <div className="container-x relative">
          <SectionHeader no="B" eyebrow="The DCC approach" title="Engineered around your operations." dark />
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="text-base leading-relaxed text-zinc-300 md:text-lg">{service.approach}</p>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-8 font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">{SITE.methodology.join(" → ")}</p>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <ListBlock title="Survey & inspection" items={service.surveyPoints} dark no="B1" />
            </div>
          </div>
        </div>
      </section>

      {/* LP2 only — verified track-record strip (homepage section) */}
      {isLp2 && <CredibilityBar />}

      {/* METHOD + TECH + SAFETY */}
      <section className="bg-offwhite py-24 md:py-32">
        <div className="container-x grid gap-14 lg:grid-cols-3">
          <ListBlock title="Cleaning methodology" items={service.methodology} no="C1" />
          <ListBlock title="Technology & equipment" items={service.technology} no="C2" />
          <ListBlock title="Site protection & safety" items={service.safety} no="C3" />
        </div>
      </section>

      {/* LP2 only — accountable process rows (homepage section) */}
      {isLp2 && <WhyDcc />}

      {/* EVIDENCE */}
      <section className="noise-dark relative overflow-hidden py-24 text-zinc-100 md:py-32">
        <div className="absolute inset-0 grid-lines-dark" aria-hidden="true" />
        <div className="container-x relative">
          <SectionHeader no="D" eyebrow="Verification & handover" title="Completion you can see." dark className="!mb-10" />
          {/* LP2 (paid ads): moving before/after evidence strip (service-specific
              image set); every other page keeps the static evidence pair */}
          {isLp2 ? (
            <EvidenceMarquee serviceId={service.id} />
          ) : (
            <BeforeAfter dark pairs={pair ? [pair] : EVIDENCE_PAIRS} />
          )}
          <Reveal delay={0.1}>
            <p className="mt-10 max-w-2xl text-sm leading-relaxed text-zinc-400">
              Every {service.name.toLowerCase()} engagement closes with the DCC Evidence Standard: photographic records, {service.id === "air-duct" ? "post-clean robotic footage, " : ""}signed checklists, system observations and a service certificate.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">The evidence you receive at handover</p>
            <ul className="mt-5 grid gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="service-evidence-list">
              {["Inspection record", "Before photographs", "Controlled execution log", "After photographs", "Completion checklist", "Service certificate", "Handover pack", "Condition observations"].map((e, ei) => (
                <li key={e} data-testid={`service-evidence-${ei + 1}`} className="bg-charcoal px-5 py-4 font-mono text-xs uppercase tracking-[0.18em] text-zinc-300">
                  <span className="mr-2 text-dcc" aria-hidden="true">▪</span>{e}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* LP2 only — evidence-standard grid + client trust/standards band (homepage sections) */}
      {isLp2 && <EvidenceScene />}
      {isLp2 && <ClientProof />}

      {/* LP2 only — moving Google-reviews strip (client-supplied review banners) */}
      {isLp2 && (
        <section className="noise-dark relative overflow-hidden py-20 text-zinc-100 md:py-24" aria-label="Client Google reviews">
          <div className="absolute inset-0 grid-lines-dark" aria-hidden="true" />
          <div className="container-x relative">
            <SectionHeader no="D2" eyebrow="Client testimonials" title="What our clients say." dark className="!mb-10" />
            <TestimonialsMarquee />
            <p className="mt-4 font-mono text-xs leading-relaxed text-zinc-400">
              // Verified Google reviews — rated 4.9/5. Hold or hover to pause.
            </p>
          </div>
        </section>
      )}

      {/* RELATED PROJECT STORIES */}
      {stories.length > 0 && (
        <section className="bg-white py-24 md:py-32">
          <div className="container-x">
            <SectionHeader no="E" eyebrow="Related project stories" title="This service, evidenced." className="!mb-8" />
            <div className="grid gap-px border border-zinc-200 bg-zinc-200 md:grid-cols-2">
              {stories.map((p) => (
                <Link key={p.id} to={`/our-work/${p.id}`} data-testid={`service-story-${p.id}`}
                  className="group flex h-full flex-col bg-white transition-colors duration-300 hover:bg-paper">
                  <div className="overflow-hidden">
                    <img src={IMG[p.image].src} alt={IMG[p.image].alt} {...imgProps(IMG[p.image].src)} loading="lazy" decoding="async"
                      className="aspect-[16/9] w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <p className="font-mono text-xs uppercase tracking-[0.2em] text-dcc-strong">{p.industry} · {p.region}</p>
                    <p className="mt-3 font-display text-lg font-semibold uppercase leading-tight tracking-tight text-charcoal">{p.title}</p>
                    <p className="link-tech mt-4 text-charcoal">Read the case study <ArrowUpRight size={13} aria-hidden="true" className="text-dcc-strong" /></p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* LP2 only — national coverage map (homepage section) */}
      {isLp2 && <LocationsCompact />}

      {/* INDUSTRIES + FAQ */}
      <section className="bg-paper py-24 md:py-32">
        <div className="container-x grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeader no="F" eyebrow="Relevant industries" title="Where this service runs." className="!mb-8" />
            <div className="flex flex-wrap gap-2">
              {related.map((ind) => (
                <Link key={ind.id} to={INDUSTRY_NAME_PAGE[ind.name] ? `/industries/${INDUSTRY_NAME_PAGE[ind.name]}` : "/industries"} data-testid={`service-industry-${ind.id}`}
                  className="border border-zinc-200 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-600 transition-colors hover:border-dcc hover:text-dcc">
                  {ind.name}
                </Link>
              ))}
            </div>
            <Reveal delay={0.1}>
              <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">Priority service locations</p>
              <div className="mt-4 flex flex-wrap gap-2" data-testid="service-locations">
                {markets.map((m) => (
                  <Link key={m.id} to={`/locations/${m.id}`} data-testid={`service-location-${m.id}`}
                    className="border border-zinc-200 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-600 transition-colors hover:border-dcc hover:text-dcc">
                    {m.name}
                  </Link>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <SectionHeader no="G" eyebrow="FAQs" title="Asked by facility teams." className="!mb-8" />
            <Accordion type="single" collapsible className="border-t border-zinc-200">
              {service.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-zinc-200">
                  <AccordionTrigger data-testid={`service-faq-${i}-trigger`} className="py-6 text-left font-display text-lg font-medium uppercase tracking-tight text-charcoal hover:text-dcc hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-sm leading-relaxed text-zinc-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* NEXT SERVICE */}
      <section className="border-t border-zinc-200 bg-offwhite py-16">
        <div className="container-x">
          <Reveal>
            <Link to={`/services/${next.slug}`} data-testid="service-next-link" className="group flex items-center justify-between gap-6">
              <div>
                <p className="eyebrow">Next service — {next.num}</p>
                <p className="mt-3 font-display text-2xl font-semibold uppercase tracking-tight text-charcoal transition-colors group-hover:text-dcc sm:text-3xl">{next.name}</p>
              </div>
              <ArrowUpRight size={32} className="shrink-0 text-zinc-300 transition-all group-hover:translate-x-1 group-hover:text-dcc" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* LP2 (paid ads): the exact homepage orange conversion scene;
          all other service pages keep the service-specific OrangeCta */}
      {isLp2 ? (
        <ConvertScene />
      ) : (
        <OrangeCta
          lines={["SEE YOUR SYSTEM.", "THEN DECIDE."]}
          copy="A DCC survey inspects the system, grades the condition with photographs, and tells you what the evidence supports. Scope follows evidence — never the other way around."
        />
      )}
    </>
  );
}
